import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { STAGE_MAP, formatMoney, formatDate, isOverdue, isToday } from '../utils/helpers';

const SOURCE_COLORS = {
  'Группа ВК': '#1d4ed8',
  'Поиск Яндекс': '#2563eb',
  'Поиск Google': '#0ea5e9',
  'Поиск Яндекс/Google': '#2563eb',   // старые записи до 23.10.2026
  'От пристава': '#3b82f6',
  'Рекомендация': '#60a5fa',
  'Сайт': '#93c5fd',
  'Канал MAX': '#7c3aed',
  'Авито': '#16a34a',
  'Яндекс Карты': '#f59e0b',
  '2ГИС': '#22c55e',
  'Google Карты': '#ef4444',
  'Яндекс Директ': '#fbbf24',
  'Листовка': '#0d9488',
  'Другое': '#94a3b8',
  'Не указан': '#cbd5e1',
};

function PieChart({ data, colorMap, formatLabel }) {
  if (!data || !data.length) return <div style={{color:'var(--text-muted)',fontSize:13,textAlign:'center',padding:20}}>Нет данных</div>;
  const total = data.reduce((s, d) => s + d.value, 0);
  if (!total) return <div style={{color:'var(--text-muted)',fontSize:13,textAlign:'center',padding:20}}>Нет данных</div>;
  let cumulative = 0;
  const slices = data.map(d => {
    const pct = d.value / total;
    const start = cumulative;
    cumulative += pct;
    return { ...d, pct, start };
  });
  const toXY = (pct) => {
    const angle = pct * 2 * Math.PI - Math.PI / 2;
    return [50 + 40 * Math.cos(angle), 50 + 40 * Math.sin(angle)];
  };
  return (
    <div style={{display:'flex',alignItems:'center',gap:20}}>
      <svg viewBox="0 0 100 100" style={{width:130,height:130,flexShrink:0}}>
        {slices.map((s, i) => {
          const [x1, y1] = toXY(s.start);
          const [x2, y2] = toXY(s.start + s.pct);
          const large = s.pct > 0.5 ? 1 : 0;
          const color = colorMap ? colorMap[s.label] || '#aaa' : s.color;
          if (s.pct >= 1) return <circle key={i} cx="50" cy="50" r="40" fill={color} />;
          return <path key={i} d={`M50,50 L${x1},${y1} A40,40 0 ${large},1 ${x2},${y2} Z`} fill={color} />;
        })}
        <circle cx="50" cy="50" r="22" fill="var(--bg-surface)" />
      </svg>
      <div style={{flex:1}}>
        {slices.map((s, i) => (
          <div key={i} style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
            <div style={{width:10,height:10,borderRadius:'50%',background:colorMap?colorMap[s.label]||'#aaa':s.color,flexShrink:0}} />
            <span style={{fontSize:13,flex:1}}>{s.label}</span>
            <span style={{fontWeight:700,fontSize:13}}>{formatLabel ? formatLabel(s.value) : s.value}</span>
            <span style={{color:'var(--text-muted)',fontSize:12}}>{Math.round(s.pct*100)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [showOverdueTasks, setShowOverdueTasks] = useState(false);
  const [showOverduePay, setShowOverduePay] = useState(false);
  const [showPlannedIncome, setShowPlannedIncome] = useState(false);
  const [hearings, setHearings] = useState([]);
  const [civilPay, setCivilPay] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/dashboard').then(r => setData(r.data)).catch(() => {});
    api.get('/cases/hearings/upcoming', { params: { days: 14 } }).then(r => setHearings(r.data)).catch(() => {});
    api.get('/cases/payments/summary').then(r => setCivilPay(r.data)).catch(() => {});
  }, []);

  if (!data) return <div style={{padding:40,textAlign:'center',color:'var(--text-muted)'}}>Загрузка...</div>;

  const stageKeys = ['consultation','documents','mfc','procedure','ip_close','completed'];

  const sourceChartData = (data.source_stats||[]).map(s => ({label: s.source, value: s.count}));

  const totalReceived = data.total_received || 0;
  const totalPending = data.total_pending || 0;
  const paymentChartData = [
    { label: 'Получено', value: totalReceived, color: '#16a34a' },
    { label: 'Ожидается (этот месяц)', value: totalPending, color: '#1d4ed8' },
    { label: 'Просрочено', value: data.total_overdue || 0, color: '#dc2626' },
  ].filter(d => d.value > 0);

  return (
    <div>
      <h1 className="page-title" style={{marginBottom:24}}>Дашборд</h1>

      {/* Оплаты по гражданским делам — отдельно от банкротной статистики */}
      {civilPay && civilPay.cases.length > 0 && (
        <div className="card" style={{marginBottom:16,padding:'12px 16px',display:'flex',gap:24,flexWrap:'wrap',alignItems:'center',cursor:'pointer'}} onClick={()=>navigate('/payments')}>
          <span style={{fontWeight:700,fontSize:14}}>Гражданские дела ↗</span>
          <span style={{fontSize:13}}>Получено в месяце: <b style={{color:'var(--green)'}}>{formatMoney(civilPay.month_paid)}</b></span>
          <span style={{fontSize:13}}>Ожидается: <b style={{color:'var(--accent)'}}>{formatMoney(civilPay.month_expected)}</b></span>
          <span style={{fontSize:13}}>Просрочено: <b style={{color:Number(civilPay.overdue)?'var(--red)':'inherit'}}>{formatMoney(civilPay.overdue)}</b></span>
        </div>
      )}

      {/* Ближайшие судебные заседания по гражданским делам */}
      {hearings.length > 0 && (
        <div className="card" style={{marginBottom:24,padding:0,overflow:'hidden',borderColor:'#0ea5e9'}}>
          <div style={{padding:'10px 16px',background:'#0ea5e914',fontWeight:700,fontSize:14,cursor:'pointer'}} onClick={()=>navigate('/hearings')}>
            Ближайшие заседания — {hearings.length} ↗
          </div>
          {hearings.slice(0,6).map(h => (
            <div key={h.id} onClick={()=>navigate(`/cases/${h.case_id}`)} style={{display:'flex',gap:12,padding:'8px 16px',borderTop:'1px solid var(--border)',cursor:'pointer',fontSize:13}}>
              <span style={{minWidth:120,fontWeight:700,color:isToday(h.hearing_date)?'var(--red)':'var(--text)'}}>{formatDate(h.hearing_date)}{h.hearing_time?` ${h.hearing_time}`:''}</span>
              <span style={{fontWeight:600}}>{h.client_name}</span>
              <span style={{color:'var(--text-muted)'}}>{h.court || h.case_court || ''}</span>
            </div>
          ))}
        </div>
      )}

      {/* Счётчики */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:16,marginBottom:24}}>
        <div className="card" style={{textAlign:'center',padding:20}}>
          <div style={{fontSize:32,fontWeight:700,color:'var(--accent)'}}>{data.total_clients}</div>
          <div style={{color:'var(--text-muted)',fontSize:13}}>Всего клиентов</div>
        </div>
        <div className="card" style={{textAlign:'center',padding:20}}>
          <div style={{fontSize:32,fontWeight:700,color:'var(--green)'}}>{data.stages?.completed||0}</div>
          <div style={{color:'var(--text-muted)',fontSize:13}}>Завершено</div>
        </div>
        <div className="card" style={{textAlign:'center',padding:20,cursor:data.overdue_tasks>0?'pointer':'default',borderColor:showOverdueTasks?'var(--red)':undefined}}
          onClick={()=>data.overdue_tasks>0&&setShowOverdueTasks(v=>!v)}>
          <div style={{fontSize:32,fontWeight:700,color:data.overdue_tasks>0?'var(--red)':'var(--text)'}}>{data.overdue_tasks}</div>
          <div style={{color:'var(--text-muted)',fontSize:13}}>Просроченных задач {data.overdue_tasks>0&&<span style={{fontSize:11}}>▾</span>}</div>
        </div>
        <div className="card" style={{textAlign:'center',padding:20,cursor:data.overdue_payments>0?'pointer':'default',borderColor:showOverduePay?'var(--red)':undefined}}
          onClick={()=>data.overdue_payments>0&&setShowOverduePay(v=>!v)}>
          <div style={{fontSize:32,fontWeight:700,color:data.overdue_payments>0?'var(--red)':'var(--text)'}}>{data.overdue_payments}</div>
          <div style={{color:'var(--text-muted)',fontSize:13}}>Просроченных оплат {data.overdue_payments>0&&<span style={{fontSize:11}}>▾</span>}</div>
        </div>
      </div>

      {/* Список просроченных задач */}
      {showOverdueTasks && data.overdue_tasks_list?.length > 0 && (
        <div className="card" style={{marginBottom:16,padding:0,overflow:'hidden',borderColor:'var(--red)'}}>
          <div style={{padding:'10px 16px',borderBottom:'1px solid var(--border)',fontWeight:700,fontSize:13,color:'var(--red)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span>Просроченные задачи — {data.overdue_tasks_list.length}</span>
            <button onClick={()=>setShowOverdueTasks(false)} style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-muted)',fontSize:18,lineHeight:1}}>×</button>
          </div>
          {data.overdue_tasks_list.map(t => (
            <div key={t.id} style={{display:'flex',gap:12,padding:'9px 16px',borderBottom:'1px solid var(--border)',fontSize:13,alignItems:'center'}}>
              <span style={{color:'var(--red)',fontWeight:600,minWidth:80,flexShrink:0}}>{formatDate(t.task_date)}</span>
              <span style={{color:'var(--accent)',fontWeight:600,cursor:'pointer',minWidth:140,flexShrink:0}} onClick={()=>navigate('/clients/'+t.client_id)}>{t.client_name}</span>
              <span style={{color:'var(--text-secondary)',flex:1}}>{t.text}</span>
            </div>
          ))}
        </div>
      )}

      {/* Список просроченных оплат */}
      {showOverduePay && data.overdue_payments_list?.length > 0 && (
        <div className="card" style={{marginBottom:16,padding:0,overflow:'hidden',borderColor:'var(--red)'}}>
          <div style={{padding:'10px 16px',borderBottom:'1px solid var(--border)',fontWeight:700,fontSize:13,color:'var(--red)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span>Просроченные оплаты — {data.overdue_payments_list.length}</span>
            <button onClick={()=>setShowOverduePay(false)} style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-muted)',fontSize:18,lineHeight:1}}>×</button>
          </div>
          {data.overdue_payments_list.map(p => (
            <div key={p.id} style={{display:'flex',gap:12,padding:'9px 16px',borderBottom:'1px solid var(--border)',fontSize:13,alignItems:'center'}}>
              <span style={{color:'var(--red)',fontWeight:600,minWidth:80,flexShrink:0}}>{formatDate(p.pay_date)}</span>
              <span style={{color:'var(--accent)',fontWeight:600,cursor:'pointer',minWidth:140,flexShrink:0}} onClick={()=>navigate('/clients/'+p.client_id+'?tab=payments')}>{p.client_name}</span>
              <span style={{color:'var(--green)',fontWeight:600,flex:1}}>{formatMoney(p.amount)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Финансы */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:16,marginBottom:24}}>
        <div className="card" style={{textAlign:'center',padding:20,cursor:'pointer',borderColor:showPlannedIncome?'var(--accent)':undefined}}
          onClick={()=>setShowPlannedIncome(v=>!v)}>
          <div style={{fontSize:22,fontWeight:700,color:'var(--accent)'}}>{formatMoney(data.planned_monthly_active)} <span style={{fontSize:11}}>▾</span></div>
          <div style={{color:'var(--text-muted)',fontSize:13}}>Ожидаемый доход / мес</div>
          <div style={{fontSize:12,color:'var(--text-muted)',marginTop:6,paddingTop:6,borderTop:'1px solid var(--border)'}}>
            Все: <strong style={{color:'var(--text)'}}>{formatMoney(data.planned_monthly_all)}</strong>
          </div>
        </div>
        <div className="card" style={{textAlign:'center',padding:20}}>
          <div style={{fontSize:22,fontWeight:700,color:'var(--text)'}}>{formatMoney(data.planned_first_active||0)}</div>
          <div style={{color:'var(--text-muted)',fontSize:13}}>Первые взносы (получено)</div>
          <div style={{fontSize:12,color:'var(--text-muted)',marginTop:6,paddingTop:6,borderTop:'1px solid var(--border)'}}>
            По графику: <strong style={{color:'var(--text)'}}>{formatMoney(data.planned_first_all||0)}</strong>
          </div>
        </div>
        <div className="card" style={{textAlign:'center',padding:20}}>
          <div style={{fontSize:22,fontWeight:700,color:'var(--green)'}}>{formatMoney(data.received_current_month||0)}</div>
          <div style={{color:'var(--text-muted)',fontSize:13}}>Получено за месяц / год</div>
          <div style={{fontSize:12,color:'var(--text-muted)',marginTop:6,paddingTop:6,borderTop:'1px solid var(--border)'}}>
            За год: <strong style={{color:'var(--text)'}}>{formatMoney(data.received_current_year||0)}</strong>
          </div>
        </div>
        <div className="card" style={{textAlign:'center',padding:20}}>
          <div style={{fontSize:22,fontWeight:700,color:data.total_overdue>0?'var(--red)':'var(--text)'}}>{formatMoney(data.total_overdue||0)}</div>
          <div style={{color:'var(--text-muted)',fontSize:13}}>Просроченные платежи</div>
        </div>
        <div className="card" style={{textAlign:'center',padding:20}}>
          <div style={{fontSize:22,fontWeight:700,color:'var(--text)'}}>{formatMoney(data.total_all_debt||0)}</div>
          <div style={{color:'var(--text-muted)',fontSize:13}}>Общий долг клиентов</div>
          <div style={{fontSize:12,color:'var(--text-muted)',marginTop:6,paddingTop:6,borderTop:'1px solid var(--border)'}}>
            Ожидается в этом месяце: <strong style={{color:'var(--accent)'}}>{formatMoney(data.total_pending||0)}</strong>
          </div>
        </div>
      </div>

      {/* Список ожидаемого дохода */}
      {showPlannedIncome && data.planned_income_list?.length > 0 && (
        <div className="card" style={{marginBottom:16,padding:0,overflow:'hidden',borderColor:'var(--accent)'}}>
          <div style={{padding:'10px 16px',borderBottom:'1px solid var(--border)',fontWeight:700,fontSize:13,color:'var(--accent)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span>Ожидаемый доход — {data.planned_income_list.length} клиентов</span>
            <button onClick={e=>{e.stopPropagation();setShowPlannedIncome(false);}} style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-muted)',fontSize:18,lineHeight:1}}>×</button>
          </div>
          {data.planned_income_list.map(c => (
            <div key={c.id} style={{display:'flex',gap:12,padding:'9px 16px',borderBottom:'1px solid var(--border)',fontSize:13,alignItems:'center'}}>
              <span style={{color:'var(--accent)',fontWeight:600,cursor:'pointer',flex:1}} onClick={()=>navigate('/clients/'+c.id)}>{c.name}</span>
              <span style={{color:'var(--green)',fontWeight:700,whiteSpace:'nowrap'}}>{formatMoney(c.monthly_payment)} / мес</span>
            </div>
          ))}
        </div>
      )}

      {/* Диаграммы */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))',gap:16,marginBottom:24}}>
        <div className="card">
          <h3 style={{fontSize:14,fontWeight:600,marginBottom:16,color:'var(--text)'}}>Источники клиентов</h3>
          <PieChart data={sourceChartData} colorMap={SOURCE_COLORS} />
        </div>
        <div className="card">
          <h3 style={{fontSize:14,fontWeight:600,marginBottom:16,color:'var(--text)'}}>Платежи: получено / ожидается</h3>
          <PieChart data={paymentChartData} formatLabel={formatMoney} />
          <div style={{marginTop:12,paddingTop:12,borderTop:'1px solid var(--border)',fontSize:13,display:'flex',justifyContent:'space-between'}}>
            <span style={{color:'var(--text-muted)'}}>Итого по графикам</span>
            <strong>{formatMoney((data.total_all_debt||0) + totalReceived)}</strong>
          </div>
        </div>
      </div>

      {/* Этапы + задачи + платежи */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))',gap:16,marginBottom:24}}>
        <div className="card">
          <h3 style={{fontSize:14,fontWeight:600,marginBottom:12,color:'var(--text)'}}>Этапы клиентов</h3>
          {stageKeys.map(k=>{const s=STAGE_MAP[k];const cnt=data.stages?.[k]||0;return(
            <div key={k} style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
              <span style={{width:8,height:8,borderRadius:'50%',background:s?.color,flexShrink:0,display:'inline-block'}} />
              <span style={{flex:1,fontSize:13}}>{s?.label}</span>
              <div style={{width:80,height:6,background:'var(--bg-hover)',borderRadius:3,overflow:'hidden'}}>
                <div style={{width:`${data.total_clients?Math.round(cnt/data.total_clients*100):0}%`,height:'100%',background:s?.color,borderRadius:3}} />
              </div>
              <span style={{fontWeight:700,fontSize:15,color:s?.color,minWidth:20,textAlign:'right'}}>{cnt}</span>
            </div>
          );})}
        </div>
        <div className="card">
          <h3 style={{fontSize:14,fontWeight:600,marginBottom:12,color:'var(--text)'}}>Ближайшие задачи</h3>
          {data.upcoming_tasks?.length?data.upcoming_tasks.slice(0,8).map(t=>(
            <div key={t.id} style={{display:'flex',gap:8,marginBottom:6,fontSize:13,borderLeft:isOverdue(t.task_date)?'3px solid var(--red)':isToday(t.task_date)?'3px solid var(--accent)':'3px solid transparent',paddingLeft:8}}>
              <span style={{color:'var(--text-muted)',minWidth:80}}>{formatDate(t.task_date)}</span>
              <span style={{cursor:'pointer',color:'var(--accent)'}} onClick={()=>navigate('/clients/'+t.client_id)}>{t.client_name}</span>
              <span style={{flex:1}}>{t.text}</span>
            </div>
          )):<div style={{color:'var(--text-muted)',fontSize:13}}>Нет задач</div>}
        </div>
        <div className="card">
          <h3 style={{fontSize:14,fontWeight:600,marginBottom:12,color:'var(--text)'}}>Ближайшие платежи</h3>
          {(()=>{
            const now = new Date(); now.setHours(0,0,0,0);
            const in14 = new Date(now); in14.setDate(now.getDate()+14);
            const list = (data.upcoming_payments||[]).filter(p=>{const d=new Date(p.pay_date);return d>=now&&d<=in14;}).slice(0,8);
            if(!list.length) return <div style={{color:'var(--text-muted)',fontSize:13}}>Нет платежей в ближайшие 14 дней</div>;
            return list.map(p=>(
              <div key={p.id} style={{display:'flex',gap:8,marginBottom:6,fontSize:13,borderLeft:isToday(p.pay_date)?'3px solid var(--accent)':'3px solid transparent',paddingLeft:8}}>
                <span style={{color:'var(--text-muted)',minWidth:80}}>{formatDate(p.pay_date)}</span>
                <span style={{cursor:'pointer',color:'var(--accent)',flex:1}} onClick={()=>navigate('/clients/'+p.client_id+'?tab=payments')}>{p.client_name}</span>
                <span style={{fontWeight:600,color:'var(--green)',whiteSpace:'nowrap'}}>{formatMoney(p.amount)}</span>
              </div>
            ));
          })()}
        </div>
      </div>

      {/* Таблица по месяцам */}
      <div className="card" style={{marginBottom:24}}>
        <h3 style={{fontSize:14,fontWeight:600,marginBottom:12,color:'var(--text)'}}>Доходы по месяцам</h3>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Месяц</th><th style={{textAlign:'right'}}>Получено</th><th style={{textAlign:'right'}}>Ожидается</th><th style={{textAlign:'right'}}>Итого</th></tr></thead>
            <tbody>
              {(data.monthly_breakdown||[]).map(row => {
                const isCurrentMonth = row.month === new Date().toISOString().slice(0,7);
                return (
                  <tr key={row.month} style={{background: isCurrentMonth ? 'var(--bg-hover)' : 'transparent'}}>
                    <td style={{fontWeight: isCurrentMonth ? 700 : 400}}>
                      {isCurrentMonth ? '▶ ' : ''}{new Date(row.month+'-01').toLocaleString('ru',{month:'long',year:'numeric'})}
                    </td>
                    <td style={{textAlign:'right',color:'var(--green)',fontWeight:600}}>{row.received > 0 ? formatMoney(row.received) : '—'}</td>
                    <td style={{textAlign:'right',color:'var(--accent)'}}>{row.planned > 0 ? formatMoney(row.planned) : '—'}</td>
                    <td style={{textAlign:'right',fontWeight:600}}>{formatMoney(row.received + row.planned)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Таблица клиентов */}
      <div className="card">
        <h3 style={{fontSize:14,fontWeight:600,marginBottom:12,color:'var(--text)'}}>Клиенты</h3>
        <div className="table-wrap"><table><thead><tr><th>Клиент</th><th>Этап</th><th>Источник</th><th>Общий долг (кредиторы)</th><th>Телефон</th><th>Дата создания</th></tr></thead><tbody>
          {data.clients?.map(c=>{const s=STAGE_MAP[c.stage]||{};return(
            <tr key={c.id} style={{cursor:'pointer'}} onClick={()=>navigate('/clients/'+c.id)}>
              <td style={{fontWeight:600}}>{c.name}</td>
              <td><span className="pill" style={{background:`${s.color}18`,color:s.color}}><span className="dot" style={{background:s.color}} />{s.label}</span></td>
              <td><span style={{fontSize:12,color:'var(--text-secondary)'}}>{c.source||'—'}</span></td>
              <td style={{fontWeight:600}}>{formatMoney(c.total_debt)}</td>
              <td>{c.phone||'—'}</td>
              <td>{formatDate(c.created_at)}</td>
            </tr>
          );})}
        </tbody></table></div>
      </div>
    </div>
  );
}
